/**
 * SDK 内联套餐列表(B6,query 模式)
 *
 * query 模式下常驻渲染(挂载即加载、日期变化自动刷新);复用 SDK RoomCard 渲染。
 * 挂载或日期变化时调 fetchPackageList(hotelId 由请求层注入,不带 token 亦可)。
 * 点套餐预订 -> onBook(由 DateBarApp 派发 book 事件 + window.open booking)。
 *
 * 用 epoch 计数忽略过期响应(快速切日期时旧请求晚到不覆盖新数据)。
 *
 * 通过 forwardRef 暴露 reload():供 DateBarApp"查询套餐"按钮手动重新拉取。
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { RoomType, PackageItem } from '@/types/room';
import { fetchPackageList } from '../api/packages';
import { SdkError } from '../errors';
import { RoomCard } from './RoomCard';

/** 不传 limit 时的默认内联房型数量(内联列表为预览意图,超出靠「查看更多房型」跳完整页) */
const DEFAULT_VISIBLE_ROOMS = 3;

/** 命令式句柄:供上层手动触发重新拉取 */
export interface PackageListRef {
  /** 用当前 checkIn/checkOut 重新拉取套餐列表 */
  reload: () => void;
}

interface PackageListProps {
  checkIn: Date;
  checkOut: Date;
  onBook: (pkg: PackageItem) => void;
  /** 内联列表最多显示的房型数量;正整数生效,不传/非正=默认 3 个 */
  limit?: number;
  /** 「查看更多房型」点击回调(跳完整列表页);仅在有套餐时渲染该按钮 */
  onViewMore: () => void;
}

export const PackageList = forwardRef<PackageListRef, PackageListProps>(function PackageList(
  { checkIn, checkOut, limit, onBook, onViewMore },
  ref
) {
  const [rooms, setRooms] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const epochRef = useRef(0);

  const load = useCallback(async () => {
    const epoch = ++epochRef.current;
    setLoading(true);
    setError(false);
    try {
      const list = await fetchPackageList(checkIn, checkOut);
      if (epochRef.current !== epoch) return;
      setRooms(list);
    } catch (e) {
      if (epochRef.current !== epoch) return;
      setError(true);
      if (!(e instanceof SdkError)) console.error('[HotelSDK] 套餐列表加载失败:', e);
    } finally {
      if (epochRef.current === epoch) setLoading(false);
    }
  }, [checkIn, checkOut]);

  // 暴露手动 reload 给上层(DateBarApp"查询套餐"按钮调用)
  useImperativeHandle(ref, () => ({ reload: () => void load() }), [load]);

  useEffect(() => {
    // 数据获取 effect(SDK 不用 react-query,手动 fetch 为合理用例)
    void load();
  }, [load]);

  if (loading) {
    return <div className="hh-list__loading">正在查询套餐…</div>;
  }
  if (error) {
    return (
      <div className="hh-list__error">
        加载失败
        <button type="button" className="hh-list__retry" onClick={load}>
          重试
        </button>
      </div>
    );
  }
  if (rooms.length === 0) {
    return <div className="hh-list__empty">暂无可用房型</div>;
  }

  // limit 截取前 N 个房型内联展示;正整数生效,不传/非正=默认 3 个;超出部分由「查看更多房型」跳完整列表页
  const visibleRooms = rooms.slice(0, limit && limit > 0 ? limit : DEFAULT_VISIBLE_ROOMS);

  return (
    <>
      <div className="hh-list">
        {visibleRooms.map((room) => (
          <RoomCard key={room.id} room={room} onBook={onBook} />
        ))}
      </div>
      <button type="button" className="hh-view-more" onClick={onViewMore}>
        查看更多房型 {'>'}
      </button>
    </>
  );
});
